import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { mediciAPI } from '../../services/api';
import { useRuoli, etichettaRuolo } from '../../utils/ruoli';
import { messaggioDa } from '../../utils/notifiche';

/**
 * Il riquadro «Utenza» della scheda del medico (richiesta di Alex del
 * 28/10/2026). Lo disegna MedicoForm solo all'amministrazione, e la rotta
 * lo rifiuta agli altri: la porta vera e' il server.
 *
 * Con un account collegato dice nome utente, ruolo, stato e secondo
 * fattore, e rimanda a «Gestione utenti» per tutto il resto (sospendere,
 * reimpostare, azzerare il secondo fattore): nessuna seconda copia di
 * quei gesti qui.
 *
 * Senza account, «Crea utenza» apre un modulo precompilato dalla scheda.
 * La creazione e' la stessa di «Gestione utenti» (services/creaUtente.js
 * nel backend): la password provvisoria parte solo per email e non passa
 * mai da questa schermata, quindi qui non c'e' nessun segreto a video da
 * trattenere.
 *
 * Tutti i pulsanti sono type="button": il riquadro sta dentro il form del
 * medico, e un invio lo salverebbe.
 */

const RUOLI_DELLA_SCHEDA = ['medico', 'medico_senior'];

/* Una proposta, non una regola: iniziale del nome e cognome, senza
   accenti, spazi e apostrofi. Chi crea la cambia se vuole. */
function proponiNomeUtente(nome, cognome) {
    const pulito = (s) => String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '')
        .toLowerCase().replace(/[^a-z0-9]/g, '');
    const n = pulito(nome);
    const c = pulito(cognome);
    return (n ? n.charAt(0) : '') + c;
}

/* Quali ruoli si offrono, secondo chi crea: il medico senior apre le procedure
   riservate e lo crea solo l'amministrazione (il server lo rifiuta comunque
   alla segreteria, services/creaUtente.js). */
const RUOLI_PER_CHI_CREA = { admin: RUOLI_DELLA_SCHEDA, segreteria: ['medico'] };

const RiquadroUtenza = ({ medico, ruoloUtente }) => {
    const medicoId = medico?.id;
    const ruoliOfferti = RUOLI_PER_CHI_CREA[ruoloUtente] || [];
    const vedeGestioneUtenti = ruoloUtente === 'admin';
    const { ruoli } = useRuoli();
    const [stato, setStato] = useState({ caricato: false, utenza: null, errore: null });
    const [modulo, setModulo] = useState(null);
    const [invio, setInvio] = useState(false);
    const [errore, setErrore] = useState(null);
    const [esito, setEsito] = useState(null);

    const carica = useCallback(async () => {
        if (!medicoId) return;
        try {
            const r = await mediciAPI.utenza(medicoId);
            setStato({ caricato: true, utenza: r.data?.data?.utenza || null, errore: null });
        } catch (e) {
            setStato({ caricato: true, utenza: null,
                errore: messaggioDa(e, 'Non riesco a leggere l\'utenza del medico.') });
        }
    }, [medicoId]);

    useEffect(() => { carica(); }, [carica]);

    const apriModulo = () => {
        setErrore(null);
        setEsito(null);
        setModulo({
            nome: medico?.nome || '',
            cognome: medico?.cognome || '',
            email: medico?.email || '',
            telefono: medico?.telefono || '',
            username: proponiNomeUtente(medico?.nome, medico?.cognome),
            ruolo: 'medico',
        });
    };

    const aggiorna = (campo, valore) => setModulo((m) => ({ ...m, [campo]: valore }));

    const crea = async () => {
        setErrore(null);
        setInvio(true);
        try {
            const r = await mediciAPI.creaUtenza(medicoId, modulo);
            const d = r.data?.data || {};
            setEsito({ emailInviata: d.email_inviata === true, destinatario: d.email_destinatario });
            setModulo(null);
            setStato({ caricato: true, utenza: d.utenza || null, errore: null });
        } catch (e) {
            setErrore(messaggioDa(e, 'Errore nella creazione dell\'utente'));
        } finally {
            setInvio(false);
        }
    };

    return (
        <fieldset className="form-section" data-riquadro="utenza">
            <legend>Utenza</legend>

            {!stato.caricato && <p className="mf-utenza-nota">Caricamento…</p>}
            {stato.errore && <div className="error-message general">{stato.errore}</div>}

            {esito && (
                <p className={esito.emailInviata ? 'mf-utenza-esito' : 'mf-utenza-esito mf-utenza-esito-ko'}>
                    {esito.emailInviata
                        ? <>Utenza creata. Le credenziali sono partite per email a <strong>{esito.destinatario}</strong>: il medico cambia la password al primo accesso.</>
                        : <>Utenza creata, ma l&apos;email a <strong>{esito.destinatario}</strong> non è partita. Il medico entra da <strong>«Password dimenticata»</strong> nella schermata di accesso.</>}
                </p>
            )}

            {stato.caricato && stato.utenza && (
                <>
                    <dl className="mf-utenza-dati">
                        <dt>Nome utente</dt><dd><code>{stato.utenza.username}</code></dd>
                        <dt>Ruolo</dt><dd>{etichettaRuolo(ruoli, stato.utenza.ruolo)}</dd>
                        <dt>Stato</dt><dd>{stato.utenza.attivo ? 'attiva' : 'sospesa'}</dd>
                        <dt>Secondo fattore</dt><dd>{stato.utenza.secondo_fattore ? 'sì' : 'no'}</dd>
                    </dl>
                    {vedeGestioneUtenti ? (
                        <p className="mf-utenza-nota">
                            Per sospenderla, reimpostare la password o azzerare il secondo fattore:{' '}
                            <Link to="/utenti">Gestione utenti</Link>.
                        </p>
                    ) : (
                        <p className="mf-utenza-nota">
                            Per sospenderla, reimpostare la password o azzerare il secondo fattore: l&apos;amministrazione.
                        </p>
                    )}
                </>
            )}

            {stato.caricato && !stato.errore && !stato.utenza && !modulo && (
                <div className="mf-utenza-vuota">
                    <p className="mf-utenza-nota">Questo medico non ha ancora un account per entrare nel gestionale.</p>
                    <button type="button" className="btn-secondary" onClick={apriModulo}>Crea utenza</button>
                </div>
            )}

            {modulo && (
                <>
                    {errore && <div className="error-message general">{errore}</div>}
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="utenza_nome">Nome</label>
                            <input id="utenza_nome" type="text" value={modulo.nome} disabled={invio}
                                onChange={(e) => aggiorna('nome', e.target.value)} />
                        </div>
                        <div className="form-group">
                            <label htmlFor="utenza_cognome">Cognome</label>
                            <input id="utenza_cognome" type="text" value={modulo.cognome} disabled={invio}
                                onChange={(e) => aggiorna('cognome', e.target.value)} />
                        </div>
                    </div>
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="utenza_email">Email</label>
                            <input id="utenza_email" type="email" value={modulo.email} disabled={invio}
                                onChange={(e) => aggiorna('email', e.target.value)} />
                        </div>
                        <div className="form-group">
                            <label htmlFor="utenza_telefono">Telefono</label>
                            <input id="utenza_telefono" type="tel" value={modulo.telefono} disabled={invio}
                                onChange={(e) => aggiorna('telefono', e.target.value)} />
                        </div>
                    </div>
                    <div className="form-row">
                        <div className="form-group">
                            <label htmlFor="utenza_username">Nome utente</label>
                            <input id="utenza_username" type="text" value={modulo.username} disabled={invio}
                                autoComplete="off" onChange={(e) => aggiorna('username', e.target.value.trim())} />
                        </div>
                        <div className="form-group">
                            <label htmlFor="utenza_ruolo">Ruolo</label>
                            <select id="utenza_ruolo" value={modulo.ruolo} disabled={invio}
                                onChange={(e) => aggiorna('ruolo', e.target.value)}>
                                {ruoliOfferti.map((v) => (
                                    <option key={v} value={v}>{etichettaRuolo(ruoli, v)}</option>
                                ))}
                            </select>
                        </div>
                    </div>
                    <p className="mf-utenza-nota">
                        La password provvisoria arriva per email al medico, che la cambia al primo accesso: tu non la vedi.
                    </p>
                    <div className="mf-utenza-azioni">
                        <button type="button" className="btn-secondary" onClick={() => setModulo(null)} disabled={invio}>
                            Annulla
                        </button>
                        <button type="button" className="btn-primary" onClick={crea} disabled={invio}>
                            {invio ? 'Creazione…' : 'Crea l\'utenza'}
                        </button>
                    </div>
                </>
            )}
        </fieldset>
    );
};

export default RiquadroUtenza;
